import { useContext, useEffect, useRef, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { PendingLoanApprovalWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import {
	Box,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { PendingLoanApprovalTable } from "../../../containers/tables/pendingloanapprovaltable";
import Cookies from "universal-cookie";
import { useQuery } from "@tanstack/react-query";
import { BaseConfirmationModal } from "../../../components/modal/confirmation";
import { camelCaseToPascalCaseFormatter } from "../../../helpers/camelCaseToPascalCaseFormatter";
import { formatAmountDisplay } from "../../../helpers/formatAmountDisplay";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { actOnPendingLoanService } from "../../../utils/api/approval/pendingloanapproval/actOnPendingLoan";
import { retrievePendingLoanService } from "../../../utils/api/loanmanagement/loansetup/retrievePendingLoan";
import { BaseButton } from "../../../components/button/styled";

export const PendingLoanApproval = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const { setActiveNavItem } = useContext(AppContext);
	const dropdownRef = useRef<HTMLUListElement | null>(null);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [action, setAction] = useState<string | null>(null);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
	const [selectedLoan, setSelectedLoan] = useState<Record<string, any> | null>(
		null,
	);
	const [formDetails, setFormDetails] = useState({
		comment: "",
	});
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Approval");
	}, [setActiveNavItem]);

	const { data: pendingLoansToBeApproved, refetch: refetchPendingLoans } =
		useQuery({
			queryKey: ["pendingLoans", TOKEN, paginationIndex],
			queryFn: async () => {
				setIsFetching(true);
				const response = await retrievePendingLoanService(
					TOKEN,
					paginationIndex,
				);
				if (!Array.isArray(response) && response?.meta) {
					setPaginationIndex((prev) => ({
						...prev,
						totalPages: String(response?.meta?.totalPages ?? "1"),
					}));
				}
				setIsFetching(false);
				return response?.data || [];
			},
			enabled: !!TOKEN,
		});

	useEffect(() => {
		if (!isConfirmationModalOpen && TOKEN) {
			refetchPendingLoans();
		}
	}, [isConfirmationModalOpen, TOKEN, refetchPendingLoans]);

	const handleSelectLoan = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		loan: Record<string, any>,
	) => {
		e.stopPropagation();
		e.preventDefault();
		return setSelectedLoan(loan);
	};

	const handleOpenConfirmationModal = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleActionItemClick = (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		selectedLoanId: string | null,
		action: string,
	) => {
		e.stopPropagation();
		e.preventDefault();
		if (!selectedLoanId) return;
		setAction(action);
		return handleOpenConfirmationModal();
	};

	const handleDropDownClickOutside = (event: MouseEvent) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			return setSelectedLoan(null);
		}
	};

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleConfirmationModalPersist = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleActionOnPendingLoan = async () => {
		try {
			const payload = {
				action,
				...(action === "Decline" ? { comment: formDetails.comment } : {}),
			};
			const response = await actOnPendingLoanService(
				TOKEN,
				selectedLoan?.institution,
				selectedLoan?.id,
				payload,
			);
			if (response.status !== "success") {
				setError(`${action} loan failed. Please try again.`);
			} else {
				setError(null);
			}
			setIsLoading(false);
			setAction(null);
			setFormDetails({
				comment: "",
			});
			setSelectedLoan(null);
			return setIsConfirmationModalOpen(false);
		} catch (error: any) {
			console.error(`${action} loan failed:`, error);
			setIsLoading(false);
			return setError(`${action} loan failed. ${error.message}`);
		}
	};

	const handleConfirmationModalCallToActionClick = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		actionButton: string,
	) => {
		e.preventDefault();
		e.stopPropagation();
		setIsLoading(true);
		if (!action || !selectedLoan) return;
		switch (actionButton) {
			case "callToActionI":
				setError(null);
				await handleActionOnPendingLoan();
				break;
			case "callToActionII":
				setIsLoading(false);
				setError(null);
				setAction(null);
				setFormDetails({
					comment: "",
				});
				setSelectedLoan(null);
				setIsConfirmationModalOpen(false);
				break;
			default:
				break;
		}
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	useEffect(() => {
		if (selectedLoan && !isConfirmationModalOpen) {
			document.addEventListener("mousedown", handleDropDownClickOutside);
		} else {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		};
	}, [selectedLoan, isConfirmationModalOpen]);

	const confirmationModalBody = (
		<Box>
			<Stack
				paddingTop={"calc(var(--basic-padding)/1.5)"}
				gap={{ mobile: "calc(var(--flex-gap)/2)" }}
			>
				{["serialNo", "obligor", "amountDisbursed", "totalPayable"].map(
					(key) => (
						<Stack
							key={key}
							direction={{ mobile: "row" }}
							gap={{ mobile: "calc(var(--flex-gap)/4)" }}
							justifyContent="space-between"
						>
							<Typography
								variant="h3"
								fontFamily={"Inter"}
								fontWeight={700}
								fontSize={16}
								lineHeight={"normal"}
							>
								{camelCaseToPascalCaseFormatter(key)}:
							</Typography>
							<Typography
								variant="body1"
								fontFamily={"Inter"}
								fontWeight={500}
								fontSize={16}
								lineHeight={"normal"}
							>
								{key === "obligor"
									? selectedLoan?.["obligor"]
											?.split(" ")
											.map(
												(word: string) =>
													word.charAt(0).toUpperCase() + word.slice(1),
											)
											.join(" ")
									: key === "amountDisbursed" || key === "totalPayable"
										? formatAmountDisplay(selectedLoan?.[key] ?? 0)
										: selectedLoan?.[key].toString().padStart(3, "0")}
							</Typography>
						</Stack>
					),
				)}
				{action === "Decline" && (
					<form>
						<BaseFieldSet sx={{ display: "flex", flexDirection: "column" }}>
							<BaseInput
								required
								multiline
								minRows={4}
								name="comment"
								value={formDetails.comment}
								placeholder="Enter reason for decline."
								onChange={(e) => handleChange(e)}
							/>
						</BaseFieldSet>
					</form>
				)}
			</Stack>
			{error && (
				<Typography
					fontFamily={"Inter"}
					fontWeight={"600"}
					fontSize={14}
					lineHeight={"normal"}
					color={"var(--text-salmon-pink-color)"}
					whiteSpace={"normal"}
					marginBlockStart={"calc(var(--basic-margin)/1)"}
				>
					{error}
				</Typography>
			)}
		</Box>
	);

	return (
		<AppLayout
			pageId={"Pending-Loan-Approval"}
			pageTitle={"Pending Loan Approval"}
		>
			<PendingLoanApprovalWrapper>
				{/* alert modal below */}
				<BaseConfirmationModal
					open={isConfirmationModalOpen}
					title="Confirm Approval"
					message="Are you sure you want to approve this loan?"
					callToActionI="Confirm"
					callToActionII="Cancel"
					isLoadingI={isLoading}
					body={confirmationModalBody}
					handleClose={handleConfirmationModalPersist}
					handleCallToAction={handleConfirmationModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box component={"div"} className="pending-loan-table-box">
					<PendingLoanApprovalTable
						rows={pendingLoansToBeApproved as Record<string, any>[]}
						dropdownRef={dropdownRef}
						selectedLoanId={selectedLoan?.id}
						handleActionButtonClick={handleSelectLoan}
						handleActionItemClick={handleActionItemClick}
					/>
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
			</PendingLoanApprovalWrapper>
		</AppLayout>
	);
};
